import { useEffect, useRef } from 'react'

import Icon from '~/components/Icon'

import { SelectionMenuProps, SelectionMenuOption } from './type'

import { MiniArrowIcon, OptionsList, OptionsListItem } from './styles'

const mockLoading: SelectionMenuProps['options'] = [
  { id: 'loading', value: '...' }
]

const SelectionMenu = ({
  statusClass = 'default',
  heightClass = 'regular',
  selectedOptions = [],
  id,
  options,
  activeOptionId,
  onSelect,
  uniselection,
  ...props
}: SelectionMenuProps) => {
  const listRef = useRef<HTMLUListElement>({
    id: '0',
    scrollTo: () => undefined
  } as HTMLUListElement)

  const itemRef = useRef<HTMLLIElement>({ clientHeight: -1 } as HTMLLIElement)

  const listCenterRef = useRef<null | number>(null)

  const isItemSelected = (selectedId: string): boolean =>
    selectedOptions.some((selected) => selected.id === selectedId)

  const isLoading = statusClass === 'processing'

  const renderOptions = () =>
    (isLoading ? mockLoading : options).map(
      (option: SelectionMenuOption, index) => (
        <OptionsListItem
          ref={index === 0 ? itemRef : null}
          id={option.id}
          key={option.id}
          role='option'
          heightClass={heightClass}
          data-active={activeOptionId === option.id}
          aria-selected={isItemSelected(option.id)}
          aria-busy={isLoading}
          tabIndex={-1}
          collapsable={option.collapsable}
          onClick={() => (isLoading ? null : onSelect(option))}
          uniselection={uniselection}
        >
          <span>{option.label || option.value}</span>

          {option.collapsable && (
            <MiniArrowIcon
              iconName='MiniArrowUp'
              icSize='extraSmall'
              collapsed={option.collapsed}
            />
          )}

          {isItemSelected(option.id) && (
            <Icon
              iconName='Close'
              icSize='extraSmall'
              onClick={(e) => {
                if (uniselection) {
                  onSelect(option, true)

                  e.stopPropagation()
                }
              }}
            />
          )}
        </OptionsListItem>
      )
    )

  useEffect(() => {
    const currentSelectedItem = listRef.current.querySelector(
      '[aria-selected=true]'
    )

    if (currentSelectedItem?.scrollIntoView) {
      currentSelectedItem.scrollIntoView({ block: 'center' })
    }
  }, [])

  useEffect(() => {
    if (!listRef.current?.scrollTo) {
      return
    }

    const activeOptionIndex = options.findIndex(
      (option) => activeOptionId === option.id
    )

    if (activeOptionIndex < 0) {
      return
    }

    if (activeOptionIndex === 0) {
      listRef.current.scrollTo(0, 0)

      return
    }

    if (activeOptionIndex === options.length - 2) {
      listRef.current.scrollTo(
        0,
        (options.length - 1) * itemRef.current.clientHeight
      )

      return
    }

    if (listCenterRef.current === null) {
      listCenterRef.current = Math.floor(
        listRef.current.clientHeight / itemRef.current.clientHeight / 2
      )
    }

    if (activeOptionIndex >= listCenterRef.current) {
      listRef.current?.scrollTo(
        0,
        (activeOptionIndex - listCenterRef.current) *
          itemRef.current.clientHeight
      )
    }
  }, [activeOptionId, options])

  return (
    <OptionsList
      role='listbox'
      aria-busy={isLoading}
      id={id}
      heightClass={heightClass}
      {...props}
      ref={listRef}
    >
      {renderOptions()}
    </OptionsList>
  )
}

export default SelectionMenu
